import type { ComponentChildren } from "preact";
import type { IsoDate } from "../domain/types.js";
import type { WeeklyCompliance } from "../domain/stats.js";
import { formatDate } from "../domain/dates.js";

/**
 * 手寫圖表，沒有函式庫——資料量小、樣式要跟 Neobrutalism 一致，
 * 引入圖表套件反而要花更多力氣覆寫它的預設樣式。
 *
 * **文字、格線與軸標都在 HTML，SVG 裡只剩資料本身。**
 *
 * 先前整張圖是一個 `viewBox="0 0 900 330"` 的 SVG，用 `width:100%` 縮放。
 * 縮放會連字一起縮：手機上卡片內寬 316px，縮放倍率 0.35，`font-size="11"`
 * 實際只畫出 3.9px，1.8px 的線只剩 0.63px。**畫面上讀不到的圖等於沒有畫。**
 *
 * 現在的分工是：
 * - 軸標、圖例、格線是 HTML，字級寫多少就是多少，不隨容器縮放。
 * - 折線的 SVG 用 `viewBox="0 0 100 100"` ＋ `preserveAspectRatio="none"`，
 *   座標一律正規化成百分比，容器多寬多高都填滿。
 * - 線寬靠 `vector-effect="non-scaling-stroke"` 固定成裝置像素，不被拉伸。
 * - 長條圖乾脆不用 SVG：一週一個 flex 群組，高度用百分比，天生就會縮放。
 *
 * 因此這裡不再需要 `pad`——留白由 CSS 的格線容器負責。
 */

/** 五條格線的位置，同時也是 y 軸標的位置。 */
const GRID_STOPS = [0, 25, 50, 75, 100];

export interface ChartSeries {
  key: string;
  label: string;
  dashed?: boolean;
}

export interface LineChartProps {
  rows: Record<string, unknown>[];
  series: ChartSeries[];
  unit: string;
  /**
   * 無單位模式：資料是正規化過的 0–1，不是公斤或百分比。
   *
   * y 軸的刻度標籤不畫（格線留著，沒有格線起伏會很難讀），上下留白也改成
   * 純比例——見下方 `margin` 的說明。
   */
  scaleless?: boolean;
}

function ChartEmpty({ message }: { message: string }) {
  return <div class="chart-empty">{message}</div>;
}

/**
 * 圖表外框：圖例在上、y 軸標在左、x 軸標在下，中間留給資料。
 *
 * 三張折線圖與一張長條圖共用它——先前四張各自在 SVG 裡重畫一次格線與軸標，
 * 字級與顏色因此各走各的。
 */
function ChartFrame({ legend, yLabels, xLabels, children }: {
  legend: ComponentChildren;
  yLabels: string[] | null;
  xLabels: ComponentChildren;
  children: ComponentChildren;
}) {
  return (
    <div class="chart">
      <div class="chart-legend">{legend}</div>
      <div class={yLabels ? "chart-body" : "chart-body chart-body-bare"}>
        {yLabels && (
          <div class="chart-y">
            {yLabels.map((text, index) => (
              <span style={{ top: `${GRID_STOPS[index]}%` }}>{text}</span>
            ))}
          </div>
        )}
        <div class="chart-plot">
          {GRID_STOPS.map(stop => <i style={{ top: `${stop}%` }} />)}
          {children}
        </div>
      </div>
      <div class="chart-x">{xLabels}</div>
    </div>
  );
}

/** 圖例的線段樣本。實線與虛線都由 CSS 的 border 畫，跟真正的線同一套顏色。 */
function LegendLine({ index, dashed, label }: { index: number; dashed?: boolean; label: string }) {
  return (
    <span>
      <i class={`legend-line legend-${index}${dashed ? " legend-dashed" : ""}`} />
      {label}
    </span>
  );
}

export function LineChart({ rows, series, unit, scaleless }: LineChartProps) {
  if (rows.length < 2) return <ChartEmpty message="至少需要 2 筆資料才會產生曲線" />;

  const values: number[] = [];
  for (const row of rows) {
    for (const s of series) {
      const value = row[s.key];
      if (Number.isFinite(value as number)) values.push(value as number);
    }
  }

  /**
   * 上下各留 16% 空間，避免曲線貼齊邊界。
   *
   * 那個下限（0.8 kg／1%）是**有單位的數字**，用來讓變化很小的日子不會被
   * 放大成雪崩。無單位模式不能套：正規化後的資料範圍就是 0–1，0.8 的留白
   * 會把曲線壓進中間 38% 的高度，起伏變得幾乎讀不出來（只用比例是 76%）。
   */
  let min = Math.min(...values);
  let max = Math.max(...values);
  const margin = scaleless
    ? (max - min) * 0.16
    : Math.max((max - min) * 0.16, unit === "%" ? 1 : 0.8);
  min -= margin;
  max += margin;

  // 正規化到 0–100：SVG 的座標系就是百分比，容器多大都對得上格線
  const x = (index: number) => index * 100 / Math.max(1, rows.length - 1);
  const y = (value: number) => (max - value) * 100 / (max - min);

  const yLabels = scaleless
    ? null
    : GRID_STOPS.map(stop => `${(max - (max - min) * stop / 100).toFixed(1)}${unit}`);

  // 只標頭、中、尾三個日期，標滿會擠成一團
  const labelIndexes = [0, Math.floor((rows.length - 1) / 2), rows.length - 1];

  return (
    <ChartFrame
      legend={series.map((s, index) => (
        <LegendLine index={index} dashed={s.dashed} label={s.label} />
      ))}
      yLabels={yLabels}
      xLabels={labelIndexes.map(index => (
        <span>{formatDate(rows[index].measurement_date as IsoDate)}</span>
      ))}
    >
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" role="img">
        {series.map((s, si) => {
          // 資料斷點時起新的 M，不要把缺口連成直線
          let d = "";
          let started = false;
          rows.forEach((row, index) => {
            const value = row[s.key];
            if (!Number.isFinite(value as number)) { started = false; return; }
            d += `${started ? "L" : "M"}${x(index).toFixed(2)},${y(value as number).toFixed(2)} `;
            started = true;
          });
          return (
            <path
              class={`series series-${si}${s.dashed ? " series-dashed" : ""}`}
              d={d}
              fill="none"
              vector-effect="non-scaling-stroke"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          );
        })}
      </svg>
    </ChartFrame>
  );
}

/**
 * 每週達標率。五個項目一組，一週一組。
 *
 * 用 HTML 的 flex 而不是 SVG 的 `<rect>`：長條圖的每一根本來就是「一個
 * 高度百分比的方塊」，交給 flex 排比自己算 x 座標簡單，而且圓角與邊框
 * 不會被容器拉伸變形。
 */
const COMPLIANCE_SERIES: [key: keyof Omit<WeeklyCompliance, "label">, label: string][] = [
  ["workout", "訓練"],
  ["protein", "三餐蛋白質"],
  ["vegetables", "蔬菜"],
  ["steps", "步數"],
  ["water", "喝水"]
];

export function ComplianceChart({ rows }: { rows: WeeklyCompliance[] }) {
  if (!rows.length) return <ChartEmpty message="尚無執行資料" />;

  return (
    <ChartFrame
      legend={COMPLIANCE_SERIES.map(([, label], index) => (
        <span><i class={`legend-swatch legend-${index}`} />{label}</span>
      ))}
      yLabels={[...GRID_STOPS].reverse().map(stop => `${stop}%`)}
      /* 每隔一週才標日期，否則標籤會重疊 */
      xLabels={rows.map((row, index) => <span class="x-group">{index % 2 === 0 ? row.label : ""}</span>)}
    >
      <div class="bars">
        {rows.map(row => (
          <div class="bar-group">
            {COMPLIANCE_SERIES.map(([key], index) => {
              const value = row[key] as number;
              // 0% 的那一根連框都不要畫：五根 2px 黑框的柱腳排在一起，會變成
              // 一排看起來像資料的黑點，而那一週其實什麼都沒有
              return <i class={`bar-${index}${value > 0 ? "" : " bar-zero"}`} style={{ height: `${value}%` }} />;
            })}
          </div>
        ))}
      </div>
    </ChartFrame>
  );
}
